import React, {useState} from 'react';
import {Box, Typography, CircularProgress} from '@mui/material';
import {NAVY, PageHeader, BandChip, UseWithCareBanner, riskBandColor, MarkerBar, MARKER_SECONDARY, RISK_BAND_COLORS} from '../components/widgets.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, whole, benchmarkOf, riskList, splitFamily} from '../networkData';
import {isSuppressedItem} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortBars} from '../../DashboardCommon/barSort';
import {riskGroupLabel} from '../../DashboardCommon/riskGroups';

/* Slide 19 — Cyber Risk Profile, renamed "Network Cyber Risk Profile" per
   the deck's "→ Network" note (SND-19-01).

     SND-19-01  risk items by family   SND.S_5.C_3   BarChartBMCard
                (prevalence %, band, Global avg., sensitive flag)

   The card ranks risks by prevalence and labels each "FAMILY · Name". Rows are
   put back in the fixed risk order and numbered from the "Select a risk" list
   (SND.S_7.C_2); a risk not on that list follows in the card's order. The
   sensitive flag is the card's own. Only the Global avg marker is drawn. */
const LEGEND = Object.keys(RISK_BAND_COLORS);

const groupRows = (RISK_ROWS) => RISK_ROWS.reduce((acc, row) => {
    const last = acc[acc.length - 1];
    if (last && last.header === row.header) {
        last.rows.push(row);
    } else {
        acc.push({header: row.header, rows: [row]});
    }
    return acc;
}, []);

const RiskRow = ({row}) => (
    <Box sx={{
        display: 'grid',
        gridTemplateColumns: {xs: '200px 1fr 40px 140px', md: '300px 1fr 40px 140px'},
        alignItems: 'center', gap: 2, mb: 1.8,
    }}>
        <Typography sx={{fontSize: '13px', color: NAVY, lineHeight: 1.3}}>
            <strong>{row.code}</strong>&nbsp;&nbsp;{row.label}
            {row.sensitive && <Typography component="span" sx={{fontSize: '11px', color: '#9CA3AF', ml: 1}}>· sensitive</Typography>}
            {/* In the value-sorted (flattened) list each row names its risk family. */}
            {row.groupLabel && <Typography component="span" sx={{display: 'block', fontSize: '11px', color: '#6A7282', mt: 0.25}}>{row.groupLabel}</Typography>}
        </Typography>
        {row.suppressed ? <Box sx={{gridColumn: '2 / span 3'}}><SuppressedValue/></Box> : (<>
        <MarkerBar score={row.pct} color={riskBandColor(row.band)} global={row.global} height={12} unit="percent"/>
        <Typography sx={{fontSize: '14px', fontWeight: 800, color: NAVY, textAlign: 'right'}}>{row.pct === null ? '—' : `${row.pct}%`}</Typography>
        <Box>{row.band && <BandChip label={row.band} color={riskBandColor(row.band)}/>}</Box>
        </>)}
    </Box>
);

const CYBER_RISK_PROFILE_SECTION_CODE = 'S_5';
const CYBER_RISK_BY_AGE_SECTION_CODE = 'S_7';

const CARD_SECTIONS = [
    CYBER_RISK_PROFILE_SECTION_CODE,
    CYBER_RISK_BY_AGE_SECTION_CODE,
];

const CyberRiskProfileTab = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)

    if (loading) {
        return <CircularLoader />;
    }

    if (!dashboardData) {
        return <NoDataAvailable />;
    }
    const order = riskList(findCard(dashboardData, CYBER_RISK_BY_AGE_SECTION_CODE, 'C_2'));
    const posOf = (name) => {
        const i = order.findIndex((r) => r.name === name);
        return i < 0 ? order.length : i;
    };

    const RISK_ROWS = (findCard(dashboardData, CYBER_RISK_PROFILE_SECTION_CODE, 'C_3')?.items || [])
        .map((item, idx) => {
            const {family, name} = splitFamily(item.label);
            return {
                idx,
                code: order.find((r) => r.name === name)?.num || '',
                label: name,
                sensitive: item.sensitive === true,
                suppressed: isSuppressedItem(item),            // a risk under the n floor: placeholder row
                pct: isSuppressedItem(item) ? null : whole(item.value),
                band: isSuppressedItem(item) ? null : item.band,
                global: benchmarkOf(item, 'Global avg.'),
                // The same heading on every dashboard (LA-ND, PD, SND, SD-03Nov-01).
                header: riskGroupLabel(name, family),
            };
        })
        .sort((a, b) => (posOf(a.label) - posOf(b.label)) || (a.idx - b.idx));
    const GROUPS = groupRows(RISK_ROWS);
    // Group order keeps the risk families and their headings; the value modes rank every risk in one list (suppressed last).
    const shownGroups = sortBars(GROUPS, sortMode, {itemsKey: 'rows'});

    return (
    <Box>
        <PageHeader
            title="Network Cyber-Risk Profile"
            subtitle="Which child online risks are most prevalent across the network, and which need action first?"
        />

        <UseWithCareBanner text="Risk prevalence is a status, not a competency score — higher means more students affected. Differences should prompt support, not assumptions. Subgroups under 10 students are suppressed."/>

        <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1}}>
                <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>Network Cyber-Risk Profile</Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                        {LEGEND.map((l) => <BandChip key={l} label={l} color={riskBandColor(l)}/>)}
                    </Box>
                    <SortToggle value={sortMode} onChange={setSortMode}/>
                </Box>
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2.5}}>
                <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
                <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Global avg</Typography>
            </Box>

            {shownGroups.map((g, gi) => (
                <Box key={`${g.header || 'all'}-${gi}`} sx={{mb: 2}}>
                    {g.header && (
                        <Typography sx={{fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#FF6900', mt: 1.5, mb: 1}}>
                            {g.header}
                        </Typography>
                    )}
                    {g.rows.map((r) => <RiskRow key={r.label} row={r}/>)}
                </Box>
            ))}
        </Box>
    </Box>
    );
};

export default CyberRiskProfileTab;
